import React, { useState } from 'react';
import { ArrowRight, ArrowLeft, Calculator, Check, Mail, ChevronRight, Send, Instagram, Facebook, Globe, Image, Camera, Video } from 'lucide-react';
import { base44 } from '@/api/base44Client';
import PublicNavbar from '@/components/landing/PublicNavbar';
import PublicFooter from '@/components/landing/PublicFooter';
import { useI18n } from '@/lib/I18nContext';

const STEPS = [
  { id: 'service', titleKey: 'calculator.step_service' },
  { id: 'networks', titleKey: 'calculator.step_networks' },
  { id: 'frequency', titleKey: 'calculator.step_frequency' },
  { id: 'content', titleKey: 'calculator.step_content' },
  { id: 'size', titleKey: 'calculator.step_size' },
  { id: 'country', titleKey: 'calculator.step_country' },
  { id: 'email', titleKey: 'calculator.step_email' },
  { id: 'result', titleKey: 'calculator.step_result' },
];

const SERVICE_OPTIONS = [
  { id: 'social_media', labelKey: 'calculator.service_social_media', descKey: 'calculator.service_social_media_desc', icon: Globe },
  { id: 'meta_ads', labelKey: 'calculator.service_meta_ads', descKey: 'calculator.service_meta_ads_desc', icon: Facebook },
  { id: 'both', labelKey: 'calculator.service_both', descKey: 'calculator.service_both_desc', icon: Instagram },
];

const FREQ_OPTIONS = [
  { id: '3', labelKey: 'calculator.freq_3', subKey: 'calculator.freq_3_sub' },
  { id: '5', labelKey: 'calculator.freq_5', subKey: 'calculator.freq_5_sub' },
  { id: '7', labelKey: 'calculator.freq_7', subKey: 'calculator.freq_7_sub' },
  { id: '10+', labelKey: 'calculator.freq_10plus', subKey: 'calculator.freq_10plus_sub' },
];

const CONTENT_OPTIONS = [
  { id: 'design', labelKey: 'calculator.content_design', descKey: 'calculator.content_design_desc', icon: Image },
  { id: 'photo', labelKey: 'calculator.content_photo', descKey: 'calculator.content_photo_desc', icon: Camera },
  { id: 'video', labelKey: 'calculator.content_video', descKey: 'calculator.content_video_desc', icon: Video },
];

const SIZE_OPTIONS = [
  { id: 'menos_5k', labelKey: 'calculator.size_menos_5k', factor: 0.8 },
  { id: '5k_20k', labelKey: 'calculator.size_5k_20k', factor: 1.0 },
  { id: '20k_100k', labelKey: 'calculator.size_20k_100k', factor: 1.3 },
  { id: 'mas_100k', labelKey: 'calculator.size_mas_100k', factor: 1.6 },
];

const COUNTRY_OPTIONS = [
  { id: 'chile', labelKey: 'calculator.country_chile', flag: '🇨🇱', factor: 1.0 },
  { id: 'mexico', labelKey: 'calculator.country_mexico', flag: '🇲🇽', factor: 0.9 },
  { id: 'colombia', labelKey: 'calculator.country_colombia', flag: '🇨🇴', factor: 0.75 },
  { id: 'argentina', labelKey: 'calculator.country_argentina', flag: '🇦🇷', factor: 0.7 },
  { id: 'espana', labelKey: 'calculator.country_espana', flag: '🇪🇸', factor: 1.15 },
  { id: 'otro', labelKey: 'calculator.country_otro', flag: '🌎', factor: 0.85 },
];

function ProgressBar({ current, total, t }) {
  const pct = Math.round((current / (total - 1)) * 100);
  return (
    <div className="w-full">
      <div className="flex justify-between text-xs mb-2" style={{ color: '#94A3B8' }}>
        <span>{t('calculator.step_label', 'Paso {current} de {total}').replace('{current}', current + 1).replace('{total}', total)}</span>
        <span>{pct}%</span>
      </div>
      <div className="h-1.5 rounded-full overflow-hidden" style={{ background: '#1E2D45' }}>
        <div
          className="h-full rounded-full transition-all duration-500 ease-out"
          style={{ width: `${pct}%`, background: 'linear-gradient(135deg, #00D5FF, #7C3AED)' }}
        />
      </div>
    </div>
  );
}

export default function CalculadoraPrecios() {
  const { t } = useI18n();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({
    service_type: null,
    social_network_count: 1,
    posting_frequency: null,
    content_creation: [],
    account_size: null,
    country: null,
    email: '',
  });
  const [email, setEmail] = useState('');
  const [result, setResult] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [emailError, setEmailError] = useState('');

  function update(key, value) {
    setAnswers(prev => ({ ...prev, [key]: value }));
  }

  function canAdvance(s) {
    switch (STEPS[s]?.id) {
      case 'service': return answers.service_type !== null;
      case 'networks': return true;
      case 'frequency': return answers.posting_frequency !== null;
      case 'content': return true;
      case 'size': return answers.account_size !== null;
      case 'country': return answers.country !== null;
      case 'email': return email.includes('@') && email.includes('.');
      default: return true;
    }
  }

  function next() {
    if (canAdvance(step)) setStep(s => Math.min(s + 1, STEPS.length - 1));
  }

  function prev() {
    setStep(s => Math.max(s - 1, 0));
  }

  function calculate() {
    const basePerNetwork = 180000; // CLP base por red social
    const freqMultiplier = { '3': 0.65, '5': 1.0, '7': 1.4, '10+': 1.9 };
    const hasContent = answers.content_creation.length > 0;
    const contentMultiplier = hasContent ? 1.4 : 0.85;
    const sizeFactor = SIZE_OPTIONS.find(s => s.id === answers.account_size)?.factor || 1;
    const countryFactor = COUNTRY_OPTIONS.find(c => c.id === answers.country)?.factor || 1;
    const serviceMultiplier = answers.service_type === 'both' ? 1.65 : answers.service_type === 'meta_ads' ? 1.3 : 1.0;
    const freqMul = freqMultiplier[answers.posting_frequency] || 1;
    const nets = answers.social_network_count;

    const base = basePerNetwork * nets * freqMul * contentMultiplier * sizeFactor * countryFactor * serviceMultiplier;
    const min = Math.round(base * 0.8);
    const max = Math.round(base * 1.3);

    return { min, max, breakdown: { nets, freqMul, contentMultiplier, sizeFactor, countryFactor, serviceMultiplier, basePerNetwork } };
  }

  async function handleSubmitEmail() {
    if (!email.includes('@') || !email.includes('.')) {
      setEmailError(t('calculator.email_error', 'Ingresa un email válido'));
      return;
    }
    setEmailError('');
    setSaving(true);

    const calc = calculate();
    setResult(calc);

    await base44.entities.PriceLead.create({
      email,
      service_type: answers.service_type,
      social_network_count: answers.social_network_count,
      posting_frequency: answers.posting_frequency,
      content_creation: answers.content_creation.join(','),
      account_size: answers.account_size,
      country: answers.country,
      price_range_min: calc.min,
      price_range_max: calc.max,
    }).catch(() => {});

    setSaving(false);
    setSaved(true);
    setAnswers(prev => ({ ...prev, email }));
    setStep(7);
  }

  const currentStep = STEPS[step];

  function formatCLP(n) { return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(n); }

  return (
    <div className="min-h-screen" style={{ background: '#070B14' }}>
      <PublicNavbar />

      <main className="pt-28 pb-20 px-4">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-12 h-12 rounded-2xl mx-auto mb-4 flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #00D5FF, #7C3AED)' }}>
              <Calculator className="w-6 h-6 text-white" />
            </div>
            <h1 className="font-heading font-bold text-2xl mb-2" style={{ color: '#F8FAFC' }}>{t('calculator.title', 'Calculadora de Precios')}</h1>
            <p className="text-sm" style={{ color: '#94A3B8' }}>{t('calculator.subtitle', 'Descubre cuánto deberías cobrar por tus servicios de marketing digital')}</p>
          </div>

          {/* Progress bar */}
          {step < 7 && <div className="mb-8"><ProgressBar current={step} total={STEPS.length} t={t} /></div>}

          {/* Step content */}
          <div className="rounded-2xl p-8 min-h-[300px]" style={{ background: '#111827', border: '1px solid #1E2D45' }}>
            {/* Step 0: Service type */}
            {currentStep.id === 'service' && (
              <div className="space-y-4">
                <h2 className="font-heading font-bold text-xl" style={{ color: '#F8FAFC' }}>{t('calculator.service_question', '¿Qué tipo de servicio necesitas cotizar?')}</h2>
                <p className="text-sm" style={{ color: '#94A3B8' }}>{t('calculator.service_desc', 'Selecciona el tipo de servicio que ofreces a tus clientes')}</p>
                <div className="grid gap-3 mt-4">
                  {SERVICE_OPTIONS.map(opt => {
                    const active = answers.service_type === opt.id;
                    return (
                      <button
                        key={opt.id}
                        onClick={() => update('service_type', opt.id)}
                        className="flex items-start gap-4 p-4 rounded-xl text-left transition-all border"
                        style={{
                          background: active ? 'rgba(0,213,255,0.08)' : '#111827',
                          borderColor: active ? 'rgba(0,213,255,0.35)' : '#1E2D45',
                          boxShadow: active ? '0 0 0 1px rgba(0,213,255,0.15)' : 'none',
                        }}
                      >
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: active ? 'linear-gradient(135deg, #00D5FF, #7C3AED)' : 'rgba(255,255,255,0.06)' }}>
                          <opt.icon className="w-5 h-5" style={{ color: active ? '#FFFFFF' : '#94A3B8' }} />
                        </div>
                        <div>
                          <p className="font-semibold" style={{ color: '#F8FAFC' }}>{t(opt.labelKey, '')}</p>
                          <p className="text-sm" style={{ color: '#94A3B8' }}>{t(opt.descKey, '')}</p>
                        </div>
                        {active && <Check className="w-5 h-5 ml-auto shrink-0" style={{ color: '#00D5FF' }} />}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Step 1: Network count slider */}
            {currentStep.id === 'networks' && (
              <div className="space-y-4">
                <h2 className="font-heading font-bold text-xl" style={{ color: '#F8FAFC' }}>{t('calculator.networks_question', '¿Cuántas redes sociales gestionarás?')}</h2>
                <p className="text-sm" style={{ color: '#94A3B8' }}>{t('calculator.networks_desc', 'Instagram, Facebook, TikTok, LinkedIn, etc. Arrastra para seleccionar')}</p>
                <div className="mt-6 space-y-4">
                  <div className="text-center">
                    <span className="text-4xl font-bold" style={{ color: '#00D5FF' }}>{answers.social_network_count}</span>
                    <span className="text-lg ml-2" style={{ color: '#94A3B8' }}>{answers.social_network_count > 1 ? t('calculator.networks_unit_plural', 'redes') : t('calculator.networks_unit_singular', 'red')}</span>
                  </div>
                  <input
                    type="range"
                    min={1} max={5} step={1}
                    value={answers.social_network_count}
                    onChange={e => update('social_network_count', parseInt(e.target.value))}
                    className="w-full h-2 rounded-full appearance-none cursor-pointer"
                    style={{ background: 'linear-gradient(90deg, #00D5FF 0%, #7C3AED 100%)' }}
                  />
                  <div className="flex justify-between text-xs px-1" style={{ color: '#94A3B8' }}>
                    {[1,2,3,4,5].map(n => (
                      <span key={n} style={{ color: answers.social_network_count === n ? '#F8FAFC' : undefined, fontWeight: answers.social_network_count === n ? 'bold' : 'normal' }}>{n}</span>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Frequency */}
            {currentStep.id === 'frequency' && (
              <div className="space-y-4">
                <h2 className="font-heading font-bold text-xl" style={{ color: '#F8FAFC' }}>{t('calculator.frequency_question', '¿Con qué frecuencia publicarás?')}</h2>
                <p className="text-sm" style={{ color: '#94A3B8' }}>{t('calculator.frequency_desc', 'Selecciona la cantidad de publicaciones por semana')}</p>
                <div className="grid gap-3 mt-4">
                  {FREQ_OPTIONS.map(opt => {
                    const active = answers.posting_frequency === opt.id;
                    return (
                      <button
                        key={opt.id}
                        onClick={() => update('posting_frequency', opt.id)}
                        className="flex items-center justify-between p-4 rounded-xl text-left transition-all border"
                        style={{
                          background: active ? 'rgba(0,213,255,0.08)' : '#111827',
                          borderColor: active ? 'rgba(0,213,255,0.35)' : '#1E2D45',
                        }}
                      >
                        <div>
                          <p className="font-semibold" style={{ color: '#F8FAFC' }}>{t(opt.labelKey, '')}</p>
                          <p className="text-xs" style={{ color: '#94A3B8' }}>{t(opt.subKey, '')}</p>
                        </div>
                        {active && <Check className="w-5 h-5" style={{ color: '#00D5FF' }} />}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Step 3: Content creation */}
            {currentStep.id === 'content' && (
              <div className="space-y-4">
                <h2 className="font-heading font-bold text-xl" style={{ color: '#F8FAFC' }}>{t('calculator.content_question', '¿Incluye creación de contenido?')}</h2>
                <p className="text-sm" style={{ color: '#94A3B8' }}>{t('calculator.content_desc', 'Selecciona todos los servicios que incluyes (puedes elegir varios)')}</p>
                <div className="grid gap-3 mt-4">
                  {CONTENT_OPTIONS.map(opt => {
                    const active = answers.content_creation.includes(opt.id);
                    return (
                      <button
                        key={opt.id}
                        onClick={() => {
                          const arr = answers.content_creation;
                          update('content_creation', active ? arr.filter(x => x !== opt.id) : [...arr, opt.id]);
                        }}
                        className="flex items-start gap-4 p-4 rounded-xl text-left transition-all border"
                        style={{
                          background: active ? 'rgba(0,213,255,0.08)' : '#111827',
                          borderColor: active ? 'rgba(0,213,255,0.35)' : '#1E2D45',
                        }}
                      >
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: active ? 'linear-gradient(135deg, #00D5FF, #7C3AED)' : 'rgba(255,255,255,0.06)' }}>
                          <opt.icon className="w-5 h-5" style={{ color: active ? '#FFFFFF' : '#94A3B8' }} />
                        </div>
                        <div>
                          <p className="font-semibold" style={{ color: '#F8FAFC' }}>{t(opt.labelKey, '')}</p>
                          <p className="text-sm" style={{ color: '#94A3B8' }}>{t(opt.descKey, '')}</p>
                        </div>
                        {active && <Check className="w-5 h-5 ml-auto shrink-0" style={{ color: '#00D5FF' }} />}
                      </button>
                    );
                  })}
                </div>
                {answers.content_creation.length === 0 && (
                  <div className="mt-3 p-3 rounded-xl text-sm text-center" style={{ background: 'rgba(255,255,255,0.04)', color: '#94A3B8' }}>
                    {t('calculator.content_empty_note', 'Si no seleccionas ninguno, se asume que el cliente entrega el contenido listo para publicar.')}
                  </div>
                )}
              </div>
            )}

            {/* Step 4: Account size */}
            {currentStep.id === 'size' && (
              <div className="space-y-4">
                <h2 className="font-heading font-bold text-xl" style={{ color: '#F8FAFC' }}>{t('calculator.size_question', '¿Cuál es el tamaño aproximado de la cuenta del cliente?')}</h2>
                <p className="text-sm" style={{ color: '#94A3B8' }}>{t('calculator.size_desc', 'A mayor audiencia, mayor complejidad y valor del servicio')}</p>
                <div className="grid gap-3 mt-4">
                  {SIZE_OPTIONS.map(opt => {
                    const active = answers.account_size === opt.id;
                    return (
                      <button
                        key={opt.id}
                        onClick={() => update('account_size', opt.id)}
                        className="flex items-center justify-between p-4 rounded-xl text-left transition-all border"
                        style={{
                          background: active ? 'rgba(0,213,255,0.08)' : '#111827',
                          borderColor: active ? 'rgba(0,213,255,0.35)' : '#1E2D45',
                        }}
                      >
                        <span className="font-semibold" style={{ color: '#F8FAFC' }}>{t(opt.labelKey, '')}</span>
                        {active && <Check className="w-5 h-5" style={{ color: '#00D5FF' }} />}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Step 5: Country */}
            {currentStep.id === 'country' && (
              <div className="space-y-4">
                <h2 className="font-heading font-bold text-xl" style={{ color: '#F8FAFC' }}>{t('calculator.country_question', '¿En qué país está tu cliente?')}</h2>
                <p className="text-sm" style={{ color: '#94A3B8' }}>{t('calculator.country_desc', 'El país ajusta el rango de precios según el mercado local')}</p>
                <div className="grid gap-3 mt-4">
                  {COUNTRY_OPTIONS.map(opt => {
                    const active = answers.country === opt.id;
                    return (
                      <button
                        key={opt.id}
                        onClick={() => update('country', opt.id)}
                        className="flex items-center gap-4 p-4 rounded-xl text-left transition-all border"
                        style={{
                          background: active ? 'rgba(0,213,255,0.08)' : '#111827',
                          borderColor: active ? 'rgba(0,213,255,0.35)' : '#1E2D45',
                        }}
                      >
                        <span className="text-2xl">{opt.flag}</span>
                        <span className="font-semibold" style={{ color: '#F8FAFC' }}>{t(opt.labelKey, '')}</span>
                        {active && <Check className="w-5 h-5 ml-auto" style={{ color: '#00D5FF' }} />}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Step 6: Email capture */}
            {currentStep.id === 'email' && (
              <div className="space-y-4">
                <div className="text-center mb-6">
                  <div className="w-14 h-14 rounded-2xl mx-auto mb-4 flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #00D5FF, #7C3AED)' }}>
                    <Mail className="w-7 h-7 text-white" />
                  </div>
                  <h2 className="font-heading font-bold text-xl" style={{ color: '#F8FAFC' }}>{t('calculator.email_title', 'Te enviamos el resultado detallado a tu correo')}</h2>
                  <p className="text-sm mt-2" style={{ color: '#94A3B8' }}>{t('calculator.email_desc', 'Ingresa tu email para recibir el desglose completo de tu cotización')}</p>
                </div>
                <div className="max-w-sm mx-auto">
                  <input
                    type="email"
                    placeholder={t('calculator.email_placeholder', 'tu@email.com')}
                    value={email}
                    onChange={e => { setEmail(e.target.value); setEmailError(''); }}
                    className="w-full px-4 py-3 rounded-xl border text-center text-lg outline-none placeholder:text-gray-500"
                    style={{ borderColor: emailError ? '#EF4444' : '#1E2D45', background: '#111827', color: '#F8FAFC' }}
                    autoFocus
                    onKeyDown={e => { if (e.key === 'Enter') handleSubmitEmail(); }}
                  />
                  {emailError && <p className="text-red-500 text-xs text-center mt-1">{emailError}</p>}
                </div>
              </div>
            )}

            {/* Step 7: Result */}
            {currentStep.id === 'result' && result && (
              <div className="space-y-6">
                <div className="text-center">
                  <div className="inline-flex items-center gap-2 text-sm font-medium mb-2" style={{ color: '#22C55E' }}>
                    <Check className="w-4 h-4" /> {t('calculator.result_sent', 'Resultado enviado a {email}').replace('{email}', email)}
                  </div>
                  <h2 className="font-heading font-bold text-xl" style={{ color: '#F8FAFC' }}>{t('calculator.result_title', 'Tu rango de precio sugerido')}</h2>
                </div>

                {/* Price card */}
                <div className="rounded-2xl p-8 text-center" style={{ background: 'linear-gradient(135deg, #00D5FF, #7C3AED)', color: '#FFFFFF' }}>
                  <p className="text-sm opacity-80 mb-1">{t('calculator.result_price_label', 'Precio mensual estimado')}</p>
                  <p className="text-4xl font-bold mb-0.5">{t('calculator.result_range', 'Entre {min} y {max}').replace('{min}', formatCLP(result.min)).replace('{max}', formatCLP(result.max))}</p>
                  <p className="text-sm opacity-70">{t('calculator.result_per_month', 'CLP / mes')}</p>
                </div>

                {/* Breakdown */}
                <div className="rounded-2xl p-5 space-y-3" style={{ background: '#111827', border: '1px solid #1E2D45' }}>
                  <h3 className="font-bold text-sm" style={{ color: '#F8FAFC' }}>{t('calculator.result_breakdown_title', '¿Cómo se calculó?')}</h3>
                  <div className="space-y-2 text-sm" style={{ color: '#CBD5E1' }}>
                    <div className="flex justify-between py-1" style={{ borderBottom: '1px solid rgba(30,45,69,0.5)' }}>
                      <span>{t('calculator.result_base_per_network', 'Base por red social')}</span>
                      <span className="font-medium">{formatCLP(result.breakdown.basePerNetwork)}</span>
                    </div>
                    <div className="flex justify-between py-1" style={{ borderBottom: '1px solid rgba(30,45,69,0.5)' }}>
                      <span>× {result.breakdown.nets} {result.breakdown.nets > 1 ? t('calculator.networks_unit_plural', 'redes') : t('calculator.networks_unit_singular', 'red')}</span>
                      <span className="font-medium">{result.breakdown.nets}</span>
                    </div>
                    <div className="flex justify-between py-1" style={{ borderBottom: '1px solid rgba(30,45,69,0.5)' }}>
                      <span>{t('calculator.result_frequency', '× Frecuencia')}</span>
                      <span className="font-medium">×{result.breakdown.freqMul.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between py-1" style={{ borderBottom: '1px solid rgba(30,45,69,0.5)' }}>
                      <span>{t('calculator.result_content', '× Creación de contenido')}</span>
                      <span className="font-medium">×{result.breakdown.contentMultiplier.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between py-1" style={{ borderBottom: '1px solid rgba(30,45,69,0.5)' }}>
                      <span>{t('calculator.result_size', '× Tamaño de cuenta')}</span>
                      <span className="font-medium">×{result.breakdown.sizeFactor.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between py-1" style={{ borderBottom: '1px solid rgba(30,45,69,0.5)' }}>
                      <span>{t('calculator.result_country', '× País')}</span>
                      <span className="font-medium">×{result.breakdown.countryFactor.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between py-1" style={{ borderBottom: '1px solid rgba(30,45,69,0.5)' }}>
                      <span>{t('calculator.result_service', '× Tipo de servicio')}</span>
                      <span className="font-medium">×{result.breakdown.serviceMultiplier.toFixed(2)}</span>
                    </div>
                  </div>
                </div>

                {/* CTA */}
                <div className="rounded-2xl p-6 text-center" style={{ background: '#0F172A', color: '#F8FAFC' }}>
                  <h3 className="font-heading font-bold text-lg mb-2">{t('calculator.cta_title', 'Gestiona estos cobros automáticamente con Bezkraen')}</h3>
                  <p className="text-sm opacity-70 mb-4">{t('calculator.cta_desc', 'Dashboards profesionales, métricas en tiempo real y reportes PDF con el logo de tu agencia')}</p>
                  <button
                    onClick={() => base44.auth.redirectToLogin()}
                    className="inline-flex items-center gap-2 font-bold px-6 py-3 rounded-xl transition-all hover:opacity-90 text-sm"
                    style={{ background: '#00D5FF', color: '#070B14' }}
                  >
                    {t('calculator.cta_button', 'Prueba gratis 7 días')} <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            {/* Navigation buttons */}
            {step < 7 && (
              <div className="flex justify-between mt-8">
                <button
                  onClick={prev}
                  disabled={step === 0}
                  className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-medium transition-all"
                  style={{ color: step === 0 ? '#475569' : '#94A3B8', cursor: step === 0 ? 'default' : 'pointer' }}
                >
                  <ArrowLeft className="w-4 h-4" /> {t('calculator.nav_back', 'Atrás')}
                </button>

                {step < 6 ? (
                  <button
                    onClick={next}
                    disabled={!canAdvance(step)}
                    className="flex items-center gap-1.5 px-5 py-2.5 rounded-xl text-sm font-bold text-white transition-all"
                    style={{
                      background: canAdvance(step) ? 'linear-gradient(135deg, #00D5FF, #7C3AED)' : '#475569',
                      cursor: canAdvance(step) ? 'pointer' : 'default',
                    }}
                  >
                    {t('calculator.nav_next', 'Siguiente')} <ArrowRight className="w-4 h-4" />
                  </button>
                ) : (
                  <button
                    onClick={handleSubmitEmail}
                    disabled={saving}
                    className="flex items-center gap-1.5 px-5 py-2.5 rounded-xl text-sm font-bold text-white transition-all"
                    style={{ background: 'linear-gradient(135deg, #00D5FF, #7C3AED)' }}
                  >
                    {saving ? t('calculator.nav_calculating', 'Calculando...') : t('calculator.nav_calculate', 'Calcular precio')} <Send className="w-4 h-4" />
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </main>

      <PublicFooter />
    </div>
  );
}